import { useEffect, useMemo } from "react";
import { Controller, useWatch, type Control, type UseFormSetValue } from "react-hook-form";
import { Trash2 } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { DemandFormValues } from "../../schema";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export interface CommodityOption {
  value: number;
  label: string;
}

export interface QualityOption {
  value: string;
  label: string;
}

export interface UnitOption {
  id: number;
  code: string;
}

interface CommodityLineRowProps {
  index: number;
  control: Control<DemandFormValues>;
  setValue: UseFormSetValue<DemandFormValues>;
  commodities: CommodityOption[];
  qualityOptions: QualityOption[];
  unitOptionsFor: (commodityId: number | null) => UnitOption[];
  currencySymbol?: string;
  canRemove: boolean;
  onRemove: () => void;
  serverError: (name: string) => string;
  t: TranslateFn;
}

const star = (label: string) => `${label} *`;

export const CommodityLineRow = ({
  index,
  control,
  setValue,
  commodities,
  qualityOptions,
  unitOptionsFor,
  currencySymbol,
  canRemove,
  onRemove,
  serverError,
  t,
}: CommodityLineRowProps) => {
  const commodityValue = useWatch({ control, name: `items.${index}.buy_commodity` });
  const unitValue = useWatch({ control, name: `items.${index}.unit_of_measure` });
  const allItems = useWatch({ control, name: "items" });

  const availableCommodities = useMemo(() => {
    const taken = new Set(
      (allItems ?? [])
        .filter((_, itemIndex) => itemIndex !== index)
        .map(item => item?.buy_commodity)
        .filter((id): id is number => id !== null && id !== undefined)
    );
    return commodities.filter(
      commodity => commodity.value === commodityValue || !taken.has(commodity.value)
    );
  }, [allItems, commodities, commodityValue, index]);

  const unitOptions = useMemo(
    () => unitOptionsFor(commodityValue ?? null),
    [unitOptionsFor, commodityValue]
  );
  const hasMultipleUnits = unitOptions.length > 1;
  const selectedUnit = unitOptions.find(unit => String(unit.id) === unitValue);
  const unitCode = selectedUnit?.code ?? (unitOptions.length === 1 ? unitOptions[0].code : "");

  // Keep the line's unit valid for its commodity: default to the only/first unit,
  // and re-pick when a commodity change makes the previous unit invalid.
  useEffect(() => {
    if (!commodityValue) {
      return;
    }
    const isCurrentValid = unitOptions.some(unit => String(unit.id) === unitValue);
    if (!isCurrentValid) {
      setValue(`items.${index}.unit_of_measure`, unitOptions[0] ? String(unitOptions[0].id) : "", {
        shouldValidate: false,
      });
    }
  }, [commodityValue, unitOptions, unitValue, index, setValue]);

  return (
    <div className="flex flex-wrap items-start gap-4">
      <Controller
        control={control}
        name={`items.${index}.buy_commodity`}
        render={({ field, fieldState }) => (
          <Field className="min-w-56 flex-1">
            <FieldLabel htmlFor={`buy_commodity-${index}`}>
              {star(t("marketOfferDetails.form.commodityLabel"))}
            </FieldLabel>
            <Select
              value={field.value ? String(field.value) : undefined}
              onValueChange={value => field.onChange(Number(value))}
            >
              <SelectTrigger
                id={`buy_commodity-${index}`}
                aria-invalid={!!fieldState.error}
                className="w-full border-border"
              >
                <SelectValue placeholder={t("marketOfferDetails.form.selectOption")} />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-72">
                {availableCommodities.map(option => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError>
              {fieldState.error?.message || serverError(`items.${index}.buy_commodity`)}
            </FieldError>
          </Field>
        )}
      />

      <Controller
        control={control}
        name={`items.${index}.quality`}
        render={({ field, fieldState }) => (
          <Field className="min-w-40 flex-1">
            <FieldLabel htmlFor={`quality-${index}`}>
              {star(t("marketOfferDetails.form.qualityLabelShort"))}
            </FieldLabel>
            <Select value={field.value || undefined} onValueChange={field.onChange}>
              <SelectTrigger
                id={`quality-${index}`}
                aria-invalid={!!fieldState.error}
                className="w-full border-border"
              >
                <SelectValue placeholder={t("marketOfferDetails.form.selectOption")} />
              </SelectTrigger>
              <SelectContent position="popper">
                {qualityOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError>
              {fieldState.error?.message || serverError(`items.${index}.quality`)}
            </FieldError>
          </Field>
        )}
      />

      <Controller
        control={control}
        name={`items.${index}.quantity`}
        render={({ field, fieldState }) => (
          <Field className="w-32">
            <FieldLabel htmlFor={`quantity-${index}`}>
              {star(t("marketOfferDetails.form.quantity"))}
            </FieldLabel>
            <div className="relative">
              <Input
                {...field}
                id={`quantity-${index}`}
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                className={`text-center border-border ${!hasMultipleUnits && unitCode ? "pr-12" : ""}`}
                value={field.value ?? ""}
                aria-invalid={!!fieldState.error}
              />
              {!hasMultipleUnits && unitCode && (
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground text-sm">
                  {unitCode}
                </span>
              )}
            </div>
            <FieldError>
              {fieldState.error?.message || serverError(`items.${index}.quantity`)}
            </FieldError>
          </Field>
        )}
      />

      {hasMultipleUnits && (
        <Controller
          control={control}
          name={`items.${index}.unit_of_measure`}
          render={({ field, fieldState }) => (
            <Field className="w-32">
              <FieldLabel htmlFor={`unit_of_measure-${index}`}>
                {star(t("marketOfferDetails.form.unitLabel"))}
              </FieldLabel>
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <SelectTrigger
                  id={`unit_of_measure-${index}`}
                  aria-invalid={!!fieldState.error}
                  onBlur={field.onBlur}
                  className="w-full border-border"
                >
                  <SelectValue placeholder={t("marketOfferDetails.form.selectOption")} />
                </SelectTrigger>
                <SelectContent position="popper">
                  {unitOptions.map(unit => (
                    <SelectItem key={unit.id} value={String(unit.id)}>
                      {unit.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError>
                {fieldState.error?.message || serverError(`items.${index}.unit_of_measure`)}
              </FieldError>
            </Field>
          )}
        />
      )}

      <Controller
        control={control}
        name={`items.${index}.price`}
        render={({ field, fieldState }) => (
          <Field className="w-36">
            <FieldLabel htmlFor={`price-${index}`}>
              {star(t("marketOfferDetails.form.pricePerUnit", { unit: unitCode }))}
            </FieldLabel>
            <div className="relative">
              {currencySymbol && (
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground text-sm">
                  {currencySymbol}
                </span>
              )}
              <Input
                {...field}
                id={`price-${index}`}
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                className="pl-8 text-center border-border"
                value={field.value ?? ""}
                aria-invalid={!!fieldState.error}
              />
            </div>
            <FieldError>
              {fieldState.error?.message || serverError(`items.${index}.price`)}
            </FieldError>
          </Field>
        )}
      />

      <WFPButton
        type="button"
        variant="ghost"
        size="icon"
        className="mt-6 text-muted-foreground"
        aria-label={t("marketOfferDetails.form.removeCommodity")}
        disabled={!canRemove}
        onClick={onRemove}
      >
        <Trash2 className="size-4" />
      </WFPButton>
    </div>
  );
};
